import { getMapbox } from './mapbox-global'
import type { Map } from 'mapbox-gl'
import {
  addRouteLayer,
  addRouteSource,
  createMapboxMarker,
  updateRouteSource,
} from './mapbox-controls'
import {
  calculateAnimationDuration,
  interpolateCoordinate,
  updateRoutesWithBezierCurve,
} from './coordinate-converters'

// eslint-disable-next-line import/default
import RouteWorker from '@/utils/workers/route-worker?worker'

export type CreateMapboxOptions = {
  accessToken: string
  mapStyle: string
  containerId: string
  camera: {
    longitude: number
    latitude: number
    zoom: number
  }
  enableTouch: boolean
  cooperativeGestures: boolean
  scrollZoom: boolean
  touchZoomRotate: boolean
}

type Destination = {
  longitude: number
  latitude: number
  title?: string | null
}

type Route = [number, number]

export function createMapbox({
  accessToken,
  mapStyle,
  containerId,
  camera,
  enableTouch,
  cooperativeGestures,
  touchZoomRotate,
}: CreateMapboxOptions): Map {
  const mapboxgl = getMapbox()
  return new mapboxgl.Map({
    accessToken,
    container: containerId,
    style: mapStyle,
    pitch: 0,
    dragPan: enableTouch,
    dragRotate: false,
    doubleClickZoom: false,
    pitchWithRotate: false,
    scrollZoom: false,
    center: camera ? [camera.longitude, camera.latitude] : [40, 10],
    zoom: camera ? camera.zoom : 5,
    attributionControl: false,
    cooperativeGestures,
    touchZoomRotate,
  })
}

export function addDestinationMarkers(
  mapbox: Map,
  destinations: Destination[],
  markerOptions: { showLabels: boolean },
) {
  destinations.map((destination, index) => {
    const label =
      markerOptions?.showLabels && destination?.title ? destination.title : ''

    return createMapboxMarker(
      mapbox,
      {
        longitude: destination.longitude,
        latitude: destination.latitude,
      },
      {
        label,
        index,
      },
    )
  })
}

export function addRouteLines(
  mapbox: Map,
  providedRoutes: Route[][],
  isAnimated: boolean,
): void {
  let animationFrameId: number

  mapbox.on('load', () => {
    const routes = providedRoutes.flat()
    const smoothCoordinates = updateRoutesWithBezierCurve(routes)

    addRouteSource(mapbox, isAnimated ? [] : smoothCoordinates)
    addRouteLayer(mapbox)

    let start: EpochTimeStamp | null = null

    const baseSpeed = 100
    const animationDuration = calculateAnimationDuration(
      smoothCoordinates,
      baseSpeed,
    )

    const animate = (timestamp: EpochTimeStamp) => {
      if (!start) start = timestamp
      const elapsed = timestamp - start
      const animationPhase = Math.min(elapsed / animationDuration, 1)

      const currentSegmentIndex = Math.floor(
        animationPhase * (smoothCoordinates.length - 1),
      )
      const segmentFraction =
        (animationPhase * (smoothCoordinates.length - 1)) % 1

      const currentCoords = smoothCoordinates.slice(0, currentSegmentIndex + 1)

      if (animationPhase < 1) {
        const interpolatedCoords = interpolateCoordinate(
          smoothCoordinates[currentSegmentIndex],
          smoothCoordinates[currentSegmentIndex + 1] ||
            smoothCoordinates[currentSegmentIndex],
          segmentFraction,
        )
        currentCoords.push(interpolatedCoords)
      }

      updateRouteSource(mapbox, currentCoords)
      animationFrameId = requestAnimationFrame(animate)
    }

    if (isAnimated && smoothCoordinates.length > 1) {
      requestAnimationFrame(animate)
    }
  })

  mapbox.on('remove', () => {
    cancelAnimationFrame(animationFrameId)
  })
}

export function hideMapTitles(mapbox: Map): void {
  mapbox.on('load', () => {
    mapbox.setLayoutProperty('country-label', 'visibility', 'none')
    mapbox.setLayoutProperty('state-label', 'visibility', 'none')
  })
}

export function addInteractiveMapStaticRouteLines(
  mapbox: Map,
  routeGroups: Route[][],
) {
  mapbox.on('load', () => {
    const smoothRouteGroups = routeGroups.map((routeGroup) =>
      updateRoutesWithBezierCurve(routeGroup),
    )

    smoothRouteGroups.forEach((smoothRouteGroup, index) => {
      addRouteSource(mapbox, smoothRouteGroup, index)
      addRouteLayer(mapbox, index)
    })
  })
}

export function addInteractiveMapRouteLines(
  mapbox: Map,
  routes: Route[][],
  activeDestinationIndex: number,
): void {
  let animationFrameId: number
  let currentIndex = activeDestinationIndex
  let allCoordinates: [number, number][] = []

  const animatedIndexes: number[] = []
  const baseSpeed = 300
  const worker = new RouteWorker()

  let pendingInterpolatedCoords: [number, number] | null = null

  const targetFPS = 30 // Limit FPS to 30
  const frameDuration = 1000 / targetFPS
  let lastFrameTime = 0

  worker.onmessage = (e: MessageEvent) => {
    const { task, result } = e.data

    if (task === 'bezierCurve') {
      animateIn(result)
    } else if (task === 'interpolateCoords') {
      pendingInterpolatedCoords = result
    }
  }

  const animateIn = (newCoordinates: [number, number][]) => {
    const animationDuration = calculateAnimationDuration(
      newCoordinates,
      baseSpeed,
    )
    let start: EpochTimeStamp | null = null

    const animate = (timestamp: EpochTimeStamp) => {
      if (!start) start = timestamp
      const elapsed = timestamp - start

      if (timestamp - lastFrameTime < frameDuration) {
        animationFrameId = requestAnimationFrame(animate)
        return
      }
      lastFrameTime = timestamp

      const animationPhase = Math.min(elapsed / animationDuration, 1)
      const currentSegmentIndex = Math.floor(
        animationPhase * (newCoordinates.length - 1),
      )
      const segmentFraction = (animationPhase * (newCoordinates.length - 1)) % 1

      const currentCoords = newCoordinates.slice(0, currentSegmentIndex + 1)

      if (animationPhase < 1) {
        if (!pendingInterpolatedCoords) {
          worker.postMessage({
            task: 'interpolateCoords',
            data: {
              from: newCoordinates[currentSegmentIndex],
              to:
                newCoordinates[currentSegmentIndex + 1] ||
                newCoordinates[currentSegmentIndex],
              fraction: segmentFraction,
            },
          })
        } else {
          currentCoords.push(pendingInterpolatedCoords)
          pendingInterpolatedCoords = null
        }
      }

      // Ensure all previous routes are also drawn
      updateRouteSource(mapbox, [...allCoordinates, ...currentCoords])

      if (animationPhase < 1) {
        animationFrameId = requestAnimationFrame(animate)
      } else {
        allCoordinates = [...allCoordinates, ...newCoordinates]
      }

      if (!animatedIndexes.includes(currentIndex)) {
        animatedIndexes.push(currentIndex)
      }
    }

    requestAnimationFrame(animate)
  }

  const handleRouteChange = (
    newIndex: number,
    initialLoad: boolean = false,
  ) => {
    if (newIndex === currentIndex || animatedIndexes.includes(newIndex)) {
      return
    }

    // Ensure previously animated routes are redrawn
    if (animatedIndexes.includes(newIndex)) {
      updateRouteSource(mapbox, allCoordinates)
      currentIndex = newIndex
      return
    }

    const previousRoute = routes[newIndex - 1] || []
    const newRoute = routes[newIndex]
    const previousRouteLastPoint = previousRoute.slice(-1)[0]

    if (newRoute.length === 0) {
      currentIndex = newIndex
      return
    }

    const newRouteCombination = previousRouteLastPoint
      ? [previousRouteLastPoint, ...newRoute]
      : newRoute

    worker.postMessage({
      task: 'bezierCurve',
      data: { route: newRouteCombination },
    })

    if (!initialLoad) {
      currentIndex = newIndex
    } else {
      animatedIndexes.push(newIndex)
    }
  }

  // @ts-expect-error - map:change event is not defined in the types
  document.addEventListener('map:change', (event: CustomEvent) => {
    const { destinationIndex } = event.detail
    handleRouteChange(destinationIndex)
  })

  mapbox.on('load', () => {
    const currentRoute = routes[currentIndex]

    if (currentIndex > 0) {
      for (let i = 0; i <= currentIndex; i++) {
        handleRouteChange(i, true)
      }
    }

    worker.postMessage({
      task: 'bezierCurve',
      data: { route: currentRoute },
    })

    animatedIndexes.push(currentIndex)
    addRouteSource(mapbox, currentRoute)
    addRouteLayer(mapbox)
  })

  mapbox.on('remove', () => {
    cancelAnimationFrame(animationFrameId)
    worker.terminate()
  })
}
